import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { Clock, SquarePen } from "lucide-react"
import type { target } from "@/modules/courses/constants/courses"
import type { Course } from "@/modules/courses/entities/courses"
import { intervalToDuration } from "date-fns"
import { convertToSeconds } from "@/shared/utils/time"
import { formatDuration } from "@/shared/utils/date-fn"
import useCoursesInfo from "@/modules/courses/hooks/use-courses-info"
import { PRODUCT_TOUR_STEPS } from "@/modules/product-tour/constants/steps"
import { useProductTour } from "@/modules/product-tour/stores/use-product-tour"
import { getProgressStatus } from "@/modules/tracking/helpers/get-progress-status"

interface CourseCardProps {
  index: number
  course: Course
  target: typeof target
  openEditGradeModal: (courseId: string) => void
  isActive: boolean
}

export default function CourseCard({
  index,
  course,
  target,
  openEditGradeModal,
  isActive: isActiveCard,
}: CourseCardProps) {
  const { isActive } = useProductTour()
  const { getCourseColor } = useCoursesInfo()
  const color = getCourseColor(course.subjectId)
  const status = getProgressStatus(course.status)
  const totalSeconds = convertToSeconds(course.remainingStudyTime)
  const duration = intervalToDuration({ start: 0, end: totalSeconds * 1000 })
  const formattedDuration = formatDuration(duration).min()

  function onOpenEditGradeModal(id: string) {
    if (isActive) {
      return
    }
    openEditGradeModal(id)
  }

  return (
    <Card
      className={cn(
        "flex h-fit min-h-[120px] w-full flex-row",
        isActiveCard ? "bg-white" : "pointer-events-none select-none bg-neutral-100",
      )}
    >
      <div className="flex h-full w-full flex-row overflow-hidden rounded-lg">
        <div
          className={cn("min-w-1", !isActiveCard && "!bg-neutral-300")}
          style={{ background: color }}
        />
        <div className="flex h-full w-full flex-col p-4">
          <div className="flex flex-row">
            <p className={cn("font-bold", !isActiveCard && "!text-neutral-400")} style={{ color }}>
              {course.name}
            </p>
          </div>
          <div className="flex w-full flex-row gap-2">
            <div className="flex flex-1 flex-col justify-end gap-1">
              <span
                onClick={() => onOpenEditGradeModal(course.id)}
                className={cn(
                  "flex w-fit cursor-pointer items-center gap-1",
                  isActive && "cursor-default",
                )}
                id={index === 0 ? PRODUCT_TOUR_STEPS.PLANNER_MODIFY_OBJECTIVE : undefined}
              >
                <p
                  className={cn(
                    "font-medium",
                    isActiveCard ? "text-neutral-600" : "text-neutral-400",
                  )}
                >
                  <span className="font-regular pr-1">Objetivo:</span>
                  <span className="font-bold">{target[course.targetObjective].value}</span>
                </p>
                <SquarePen
                  size={18}
                  className={cn(
                    "text-neutral-600 transition-colors hover:text-neutral-800",
                    !isActiveCard && "text-neutral-400",
                  )}
                />
              </span>
              <span className="flex items-center gap-1">
                <Clock
                  className={cn("h-4 w-4", isActiveCard ? "text-neutral-600" : "text-neutral-400")}
                />
                <p
                  className={cn(
                    "font-normal",
                    isActiveCard ? "text-neutral-600" : "text-neutral-400",
                  )}
                >
                  {formattedDuration}
                </p>
              </span>
            </div>
            <div className="flex-2 items-end justify-end">
              <div className="flex flex-col items-center gap-1">
                <div
                  className={cn(
                    "flex rounded-full p-1",
                    isActiveCard ? status.color : "bg-neutral-200",
                  )}
                >
                  <img
                    className={cn("h-6 w-6 max-w-6", isActiveCard ? "" : "opacity-50 grayscale")}
                    src={status.image}
                    alt={status.label}
                  />
                </div>
                <p
                  className={cn(
                    "text-sm font-normal",
                    isActiveCard ? "text-neutral-600" : "text-neutral-400",
                  )}
                >
                  {status.label}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
